<script>
/**
 * WatchlistDecisionModal — one modal, two modes (`clear` / `block`).
 *
 * Wired: each file uploads directly to S3 via a pre-signed PUT (presignWatchlistDocument -> native fetch
 * PUT); confirm posts the collected documentIds + notes to watchlist-clear / watchlist-block.
 *
 * S3 PUT uses native fetch (NOT axios): the app sets axios.defaults.headers Authorization globally, which
 * S3 rejects as "Only one auth mechanism allowed" alongside the signed query string. fetch inherits nothing.
 *
 * Modes:
 *   clear -> "Upload Supporting Approval Documents",  notes = Approval Notes,            confirm = "Upload & Approve KYC"
 *   block -> "Upload Supporting Enforcement & Block Documents", notes = Blocking Regulatory Notes, confirm = "Upload & Block Merchant"
 */
import { peddlrWalletKycLiteMethods } from "@/state/helpers";

const WL_ACCEPTED_TYPES = ["image/jpeg", "image/png"];
const WL_MAX_FILE_BYTES = 15 * 1024 * 1024;

function wlFormatFileSize(bytes) {
  if (bytes === null || bytes === undefined) return "";
  const mb = bytes / (1024 * 1024);
  if (mb >= 1) return `${mb.toFixed(1)} MB`;
  return `${(bytes / 1024).toFixed(0)} KB`;
}

export default {
  name: "WatchlistDecisionModal",
  props: {
    // v-model: controls visibility
    value: { type: Boolean, default: false },
    // 'clear' | 'block'
    mode: { type: String, default: "clear" },
    // ref_business.account_id of the merchant being resolved
    businessId: { type: [String, Number], default: null },
  },
  data() {
    return {
      // Each entry: { documentId, fileName, sizeBytes, progress, uploading, error }
      files: [],
      notes: "",
      isDragging: false,
      submitting: false,
    };
  },
  computed: {
    show: {
      get() {
        return this.value;
      },
      set(v) {
        this.$emit("input", v);
      },
    },
    isBlock() {
      return this.mode === "block";
    },
    title() {
      return this.isBlock
        ? "Upload Supporting Enforcement & Block Documents"
        : "Upload Supporting Approval Documents";
    },
    subtitle() {
      return this.isBlock
        ? "To block this merchant, please attach required regulatory enforcement evidence."
        : "To approve and clear this watchlist match, please upload supporting documents for this user.";
    },
    notesLabel() {
      return this.isBlock ? "Blocking Regulatory Notes" : "Approval Notes";
    },
    confirmLabel() {
      return this.isBlock ? "Upload & Block Merchant" : "Upload & Approve KYC";
    },
    attachedCount() {
      return this.files.filter((f) => !f.uploading && !f.error).length;
    },
    hasPendingUpload() {
      return this.files.some((f) => f.uploading);
    },
    // Confirm is enabled only when notes are present AND at least one file finished uploading.
    canConfirm() {
      return (
        this.notes.trim().length > 0 &&
        this.attachedCount > 0 &&
        !this.hasPendingUpload &&
        !this.submitting
      );
    },
  },
  watch: {
    // Reset the form each time the modal opens so a prior session never leaks in.
    value(opened) {
      if (opened) {
        this.files = [];
        this.notes = "";
        this.isDragging = false;
        this.submitting = false;
      }
    },
  },
  methods: {
    ...peddlrWalletKycLiteMethods,
    formatSize(bytes) {
      return wlFormatFileSize(bytes);
    },
    onBrowse() {
      this.$refs.fileInput.click();
    },
    onFileInputChange(e) {
      this.addFiles(Array.from(e.target.files || []));
      // allow re-selecting the same file name
      e.target.value = "";
    },
    onDrop(e) {
      this.isDragging = false;
      this.addFiles(Array.from(e.dataTransfer.files || []));
    },
    addFiles(fileList) {
      for (const file of fileList) {
        if (!WL_ACCEPTED_TYPES.includes(file.type)) {
          this.$toast && this.$toast.showToast
            ? this.$toast.showToast("error", "Unsupported file", `${file.name} is not a JPEG or PNG.`)
            : null;
          continue;
        }
        if (file.size > WL_MAX_FILE_BYTES) {
          this.$toast && this.$toast.showToast
            ? this.$toast.showToast("error", "File too large", `${file.name} exceeds 15MB.`)
            : null;
          continue;
        }
        const entry = {
          documentId: null,
          fileName: file.name,
          sizeBytes: file.size,
          progress: 0,
          uploading: true,
          error: false,
        };
        this.files.push(entry);
        this.uploadFile(file, entry);
      }
    },
    // Direct-to-S3: presign -> PUT the bytes with native fetch (no app auth header leaks into the S3 PUT).
    async uploadFile(file, entry) {
      try {
        const res = await this.presignWatchlistDocument({
          businessId: this.businessId,
          fileName: file.name,
          contentType: file.type,
          sizeBytes: file.size,
        });
        if (!res || !res.uploadUrl) {
          throw new Error("Presign returned no uploadUrl.");
        }
        entry.documentId = res.documentId;

        const putRes = await fetch(res.uploadUrl, {
          method: "PUT",
          headers: { "Content-Type": file.type || "application/octet-stream" },
          body: file,
        });
        if (!putRes.ok) {
          const bodyText = await putRes.text().catch(() => "");
          console.error("S3 PUT rejected:", putRes.status, bodyText);
          throw new Error(`S3 PUT ${putRes.status}`);
        }

        entry.uploading = false;
        entry.progress = 100;
      } catch (err) {
        console.log("Watchlist document upload failed:", err);
        entry.uploading = false;
        entry.error = true;
        const idx = this.files.indexOf(entry);
        if (idx > -1) this.files.splice(idx, 1);
        this.$toast && this.$toast.showToast
          ? this.$toast.showToast("error", "Upload failed", `Could not upload ${file.name}. Please try again.`)
          : null;
      }
    },
    removeFile(index) {
      this.files.splice(index, 1);
    },
    onCancel() {
      this.show = false;
      this.$emit("cancel");
    },
    onConfirm() {
      if (!this.canConfirm) return;
      this.submitting = true;
      const documentIds = this.files
        .filter((f) => !f.uploading && !f.error && f.documentId)
        .map((f) => f.documentId);
      const notes = this.notes.trim();

      const action = this.isBlock
        ? this.blockWatchlist({ businessId: this.businessId, blockingNotes: notes, documentIds })
        : this.clearWatchlist({ businessId: this.businessId, approvalNotes: notes, documentIds });

      action
        .then((message) => {
          this.submitting = false;
          this.$emit("confirmed", {
            mode: this.mode,
            notes,
            documentIds,
            result: message,
          });
          this.show = false;
        })
        .catch((err) => {
          this.submitting = false;
          const msg =
            (err && (err.description || err.message)) ||
            (typeof err === "string" ? err : "Unable to complete the decision. Please try again.");
          this.$emit("failed", msg);
        });
    },
  },
};
</script>

<template>
  <b-modal
    v-model="show"
    size="lg"
    centered
    hide-header
    hide-footer
    no-close-on-backdrop
  >
    <div class="wl-modal">
      <div class="d-flex justify-content-between align-items-start mb-1">
        <h5 class="fw-bold mb-0">{{ title }}</h5>
        <button type="button" class="btn-close-x" @click="onCancel" aria-label="Close">
          <i class="bx bx-x"></i>
        </button>
      </div>
      <p class="text-muted small mb-3">{{ subtitle }}</p>

      <!-- Dropzone -->
      <div
        class="wl-dropzone"
        :class="{ dragging: isDragging }"
        @dragover.prevent="isDragging = true"
        @dragleave.prevent="isDragging = false"
        @drop.prevent="onDrop"
        @click="onBrowse"
      >
        <div class="wl-dropzone-icon">
          <i class="bx bxs-file-image"></i>
        </div>
        <p class="mb-1 mt-2">
          Drag &amp; drop supporting files here, or
          <a href="javascript:void(0);" class="wl-browse" @click.stop="onBrowse">Browse Files</a>
        </p>
        <p class="small text-muted mb-0">
          Supported formats: <b>JPEG, PNG</b> (Multiple files allowed) · Max 15MB per file
        </p>
        <input
          ref="fileInput"
          type="file"
          class="d-none"
          accept="image/jpeg,image/png"
          multiple
          @change="onFileInputChange"
        />
      </div>

      <!-- Uploaded files -->
      <div class="mt-3" v-if="files.length">
        <div class="d-flex justify-content-between">
          <span class="fw-semibold">Uploaded Files:</span>
          <span class="text-muted small">({{ attachedCount }} attached)</span>
        </div>
        <div class="wl-file-row" v-for="(file, index) in files" :key="index">
          <i class="bx bxs-file wl-file-icon"></i>
          <div class="wl-file-body">
            <div class="wl-file-name">{{ file.fileName }}</div>
            <div class="wl-file-size small text-muted" v-if="!file.uploading">
              {{ formatSize(file.sizeBytes) }}
            </div>
            <div class="wl-progress" v-else>
              <div class="wl-progress-bar" :style="{ width: file.progress + '%' }"></div>
            </div>
          </div>
          <button
            type="button"
            class="wl-file-remove"
            @click="removeFile(index)"
            aria-label="Remove file"
          >
            <i class="bx" :class="file.uploading ? 'bx-x' : 'bx-trash'"></i>
          </button>
        </div>
      </div>

      <!-- Notes -->
      <div class="mt-3">
        <label class="fw-semibold mb-1">{{ notesLabel }}<span class="text-danger">*</span></label>
        <b-form-textarea
          v-model="notes"
          placeholder="Enter notes..."
          rows="3"
          max-rows="5"
        ></b-form-textarea>
      </div>

      <!-- Actions -->
      <div class="d-flex mt-3">
        <b-button
          type="button"
          class="wl-confirm-btn me-2"
          :class="{ block: isBlock }"
          :disabled="!canConfirm"
          @click="onConfirm"
        >
          {{ confirmLabel }}
        </b-button>
        <b-button type="button" variant="light" class="wl-cancel-btn" @click="onCancel">
          Cancel
        </b-button>
      </div>
    </div>
  </b-modal>
</template>

<style scoped>
.wl-modal {
  padding: 0.25rem 0.25rem 0.5rem;
}
.btn-close-x {
  border: none;
  background: transparent;
  font-size: 22px;
  line-height: 1;
  color: #6c757d;
  cursor: pointer;
}
.wl-dropzone {
  border: 2px dashed #c4aacf; /* primary-200 */
  border-radius: 12px;
  padding: 1.75rem 1rem;
  text-align: center;
  cursor: pointer;
  background: #fafafa;
  transition: background 0.15s ease, border-color 0.15s ease;
}
.wl-dropzone.dragging {
  background: #fdeaf4; /* secondary-50 */
  border-color: #7f4697; /* primary-500 */
}
.wl-dropzone-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: #fdeaf4; /* secondary-50 */
}
.wl-dropzone-icon .bx {
  font-size: 24px;
  color: #e83092; /* secondary-500 */
}
.wl-browse {
  color: #7f4697; /* primary-500 */
  font-weight: 700;
  text-decoration: underline;
}
.wl-file-row {
  display: flex;
  align-items: center;
  padding: 0.6rem 0;
  border-bottom: 1px solid #f0f0f0;
}
.wl-file-icon {
  font-size: 22px;
  color: #e83092; /* secondary-500 */
  margin-right: 0.6rem;
}
.wl-file-body {
  flex: 1;
  min-width: 0;
}
.wl-file-name {
  font-weight: 500;
  font-size: 14px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.wl-progress {
  height: 6px;
  background: #eee;
  border-radius: 4px;
  overflow: hidden;
  margin-top: 4px;
}
.wl-progress-bar {
  height: 100%;
  background: #7f4697; /* primary-500 */
  transition: width 0.2s ease;
}
.wl-file-remove {
  border: none;
  background: transparent;
  color: #c0392b;
  font-size: 18px;
  cursor: pointer;
}
.wl-confirm-btn {
  background-color: #3ba55d;
  border-color: #3ba55d;
  color: #fff;
  min-width: 180px;
}
.wl-confirm-btn.block {
  background-color: #a12d2a;
  border-color: #a12d2a;
}
.wl-confirm-btn:disabled {
  background-color: #cfcfcf;
  border-color: #cfcfcf;
  color: #fff;
}
.wl-cancel-btn {
  border: 1px solid #d9d9d9;
}
</style>
